import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  applyTemplateToUser,
  createRoleTemplate,
  deleteRoleTemplate,
  getUserPermissions,
  listDeletedSystemTemplates,
  listRoleTemplates,
  putUserPermissions,
  restoreSystemTemplate,
  updateRoleTemplate,
  type RoleTemplate,
  type RoleTemplateInput,
  type UserPermissionMatrix,
} from '../lib/permissionsApi';

const ROLE_TEMPLATES_KEY = ['role-templates'] as const;
const DELETED_SYSTEM_KEY = ['role-templates', 'system', 'deleted'] as const;

export function useRoleTemplates() {
  return useQuery({
    queryKey: ROLE_TEMPLATES_KEY,
    queryFn: listRoleTemplates,
  });
}

export function useUserPermissions(userId: string | undefined) {
  return useQuery({
    queryKey: ['user-permissions', userId],
    queryFn: () => getUserPermissions(userId as string),
    enabled: Boolean(userId),
  });
}

export function useCreateRoleTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RoleTemplateInput) => createRoleTemplate(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROLE_TEMPLATES_KEY });
    },
  });
}

export function useUpdateRoleTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<RoleTemplateInput> }) =>
      updateRoleTemplate(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROLE_TEMPLATES_KEY });
      // Editing a template now re-syncs every account assigned to it server-side
      // (CLE-431), so any cached per-user matrices — and the current admin's own
      // resolved matrix, if they edited a template they're on — may be stale.
      void qc.invalidateQueries({ queryKey: ['user-permissions'] });
      void qc.invalidateQueries({ queryKey: ['permissions', 'me'] });
    },
  });
}

export function useDeleteRoleTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, replaceWith }: { id: string; replaceWith?: string }) =>
      deleteRoleTemplate(id, replaceWith),
    onSuccess: () => {
      // Also refreshes the deleted-system list (same key prefix).
      void qc.invalidateQueries({ queryKey: ROLE_TEMPLATES_KEY });
      // Moved users got the replacement template's matrix.
      void qc.invalidateQueries({ queryKey: ['user-permissions'] });
      void qc.invalidateQueries({ queryKey: ['permissions', 'me'] });
    },
  });
}

export function useDeletedSystemTemplates(enabled = true) {
  return useQuery({
    queryKey: DELETED_SYSTEM_KEY,
    queryFn: listDeletedSystemTemplates,
    enabled,
  });
}

export function useRestoreSystemTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => restoreSystemTemplate(key),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROLE_TEMPLATES_KEY });
    },
  });
}

export function useApplyTemplateToUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleTemplateId }: { userId: string; roleTemplateId: string }) =>
      applyTemplateToUser(userId, roleTemplateId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['user-permissions', vars.userId] });
      void qc.invalidateQueries({ queryKey: ['permissions', 'me', vars.userId] });
    },
  });
}

export function usePutUserPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      permissions,
      roleTemplateId,
    }: {
      userId: string;
      permissions?: UserPermissionMatrix | null;
      roleTemplateId?: string | null;
    }) => putUserPermissions(userId, { permissions, roleTemplateId }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['user-permissions', vars.userId] });
      void qc.invalidateQueries({ queryKey: ['permissions', 'me', vars.userId] });
    },
  });
}

export type { RoleTemplate };
